import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";
import { buildSiteFilterObject } from "./BuildSiteFilterObject";
import { useFileDownload } from "../../ui/useFileDownload";

type ListSitesResponse =
  paths["/sites"]["get"]["responses"]["200"]["content"]["application/json"];

export type SiteListItem = ListSitesResponse["items"][number];

type MethodStatusFilter =
  NonNullable<paths["/sites"]["get"]["parameters"]["query"]>["articleXmlRpcStatus"];

type AccessValidityFilter =
  NonNullable<paths["/sites"]["get"]["parameters"]["query"]>["accessValidity"];

type SiteTypeFilter =
  NonNullable<paths["/sites"]["get"]["parameters"]["query"]>["siteType"];

type ExportAccessStateRequest =
  paths["/sites/export-access-state"]["post"]["requestBody"]["content"]["application/json"];

export type ExportSelection = ExportAccessStateRequest["selection"];

export type SiteFilterParams = Omit<ExportAccessStateRequest, "selection" | "siteIds">;

export interface SiteListFilters {
  search: string;
  region: string;
  // undefined — фильтр по типу сайта не выставлен: в выдаче остаются и домены,
  // и субдомены.
  siteType: SiteTypeFilter | undefined;
  // undefined — фильтр по годности доступов не выставлен: в выдаче остаются
  // сайты с любым состоянием доступа.
  accessValidity: AccessValidityFilter | undefined;
  articleXmlRpcStatus: MethodStatusFilter;
  articleApiStatus: MethodStatusFilter;
  articleWpAdminStatus: MethodStatusFilter;
  homepageApiStatus: MethodStatusFilter;
  homepageWpAdminStatus: MethodStatusFilter;
  linkRemovalApiStatus: MethodStatusFilter;
  linkRemovalXmlRpcStatus: MethodStatusFilter;
  linkRemovalWpAdminStatus: MethodStatusFilter;
  // undefined — фильтр по состоянию плагина для статей не выставлен: сайты
  // остаются в выдаче при любом его состоянии.
  pluginArticleStatusFilter: "installed" | "valid" | "invalid" | undefined;
  // undefined — фильтр по состоянию плагина для главной не выставлен.
  pluginHomepageStatusFilter: "installed" | "valid" | "invalid" | undefined;
  // undefined — фильтр по состоянию сквозного плагина не выставлен.
  pluginSitewideStatusFilter: "installed" | "valid" | "invalid" | undefined;
  // undefined — фильтр по состоянию плагина снятия ссылок не выставлен.
  pluginLinkRemovalStatusFilter: "installed" | "valid" | "invalid" | undefined;
  seller: string;
  dateFrom: string;
  dateTo: string;
  minArticlePostsCount: string;
  minHomepagePostsCount: string;
  maxArticleAttemptErrors: string;
  maxHomepageAttemptErrors: string;
  withoutParams: boolean;
}

export const EMPTY_FILTERS: SiteListFilters = {
  search: "",
  region: "",
  siteType: undefined,
  accessValidity: undefined,
  articleXmlRpcStatus: undefined,
  articleApiStatus: undefined,
  articleWpAdminStatus: undefined,
  homepageApiStatus: undefined,
  homepageWpAdminStatus: undefined,
  linkRemovalApiStatus: undefined,
  linkRemovalXmlRpcStatus: undefined,
  linkRemovalWpAdminStatus: undefined,
  pluginArticleStatusFilter: undefined,
  pluginHomepageStatusFilter: undefined,
  pluginSitewideStatusFilter: undefined,
  pluginLinkRemovalStatusFilter: undefined,
  seller: "",
  dateFrom: "",
  dateTo: "",
  minArticlePostsCount: "",
  minHomepagePostsCount: "",
  maxArticleAttemptErrors: "",
  maxHomepageAttemptErrors: "",
  withoutParams: false,
};

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: SiteListItem[]; totalCount: number }
  | { kind: "error"; message: string };

type ExportState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

const DEFAULT_PAGE_SIZE = 25;

function buildSiteFilterParams(filters: SiteListFilters): URLSearchParams {
  const parameters = new URLSearchParams();
  for (const [key, value] of Object.entries(buildSiteFilterObject(filters))) {
    parameters.set(key, String(value));
  }
  return parameters;
}

export interface UseSiteListResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  filters: SiteListFilters;
  exportState: ExportState;
  setFilters: (filters: SiteListFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refetch: () => void;
  exportAccessState: (selection: ExportSelection, siteIds: string[]) => void;
}

export function useSiteList(): UseSiteListResult {
  const downloadFile = useFileDownload();
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [filters, setFiltersState] = useState<SiteListFilters>(EMPTY_FILTERS);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [exportState, setExportState] = useState<ExportState>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadDone = useRef(false);

  const fetchSites = useCallback(
    async (currentFilters: SiteListFilters, currentPage: number, currentPageSize: number) => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (!initialLoadDone.current) {
        setState({ kind: "loading" });
      }

      setFetching(true);

      const parameters = buildSiteFilterParams(currentFilters);
      parameters.set("page", String(currentPage));
      parameters.set("pageSize", String(currentPageSize));

      try {
        const response = await fetchWithRefresh(`/api/sites?${parameters.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as ListSitesResponse;

        initialLoadDone.current = true;

        if (data.items.length === 0 && data.totalCount === 0) {
          setState({ kind: "empty" });
        } else {
          setState({
            kind: "data",
            items: data.items,
            totalCount: data.totalCount,
          });
        }
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to fetch sites");
        setState({ kind: "error", message });
      } finally {
        if (!controller.signal.aborted) {
          setFetching(false);
        }
      }
    },
    [],
  );

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const pageRef = useRef(page);
  pageRef.current = page;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  useEffect(() => {
    void fetchSites(filters, page, pageSize);
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [filters, page, pageSize, fetchSites]);

  const refetch = useCallback(() => {
    void fetchSites(filtersRef.current, pageRef.current, pageSizeRef.current);
  }, [fetchSites]);

  const setFilters = useCallback((newFilters: SiteListFilters) => {
    setFiltersState(newFilters);
    setPageState(1);
  }, []);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1);
  }, []);

  const exportAccessState = useCallback((selection: ExportSelection, siteIds: string[]) => {
    setExportState({ kind: "loading" });

    const body: ExportAccessStateRequest = {
      selection,
      ...buildSiteFilterObject(filtersRef.current),
      // Список всегда назван явно: остальные режимы выбирают сайты фильтром и шлют пустой.
      siteIds: selection === "selected" ? siteIds : [],
    };

    fetchWithRefresh(`/api/sites/export-access-state`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        const fileNameMatch = contentDisposition
          ? contentDisposition.match(/filename="(.+)"/)
          : null;
        const fileName = fileNameMatch ? fileNameMatch[1] : "access-state.csv";

        downloadFile(blob, fileName);

        setExportState({ kind: "idle" });
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : "Export failed";
        logger.error({ err: message }, "Failed to export access state");
        setExportState({ kind: "error", message });
      });
  }, []);

  return { state, fetching, page, pageSize, filters, exportState, setFilters, setPage, setPageSize, refetch, exportAccessState };
}
